import React, { useMemo, useState, useRef, useEffect } from 'react';
import { PAGE_DIMS } from './canvas/constants';
import { resolveLabel } from '@/lib/fieldLabel';
import { VisualErrorBoundary } from './VisualErrorBoundary';

// Deterministic row metrics (px) used to page a spanning table without DOM
// measurement — keeps screen, print and PDF pagination identical.
const HEADER_ROW_H = 34;
const BODY_ROW_H = 30;
const CONTINUED_LABEL_H = 22;

function fmtFooter(text, pageIdx, total) {
  return (text || 'Page {{page}} of {{total_pages}}')
    .replace(/\{\{page\}\}/g, String(pageIdx + 1))
    .replace(/\{\{total_pages\}\}/g, String(total))
    .replace(/\{\{date\}\}/g, new Date().toLocaleDateString());
}

function fmtTitle(title, reportName) {
  return (title || '{{report_name}}').replace(/\{\{report_name\}\}/g, reportName || '');
}

// Build the ordered list of physical pages. Each entry references an authored
// page and, for spanning tables, carries the row-slice to render (Flow / Option A:
// the table expands to the bottom margin then continues on inserted pages; any
// visuals placed below the table on its starting page come after it finishes).
// Expand a table's flat result rows into the ordered list of render rows the
// table actually shows — interleaving group headers, per-group subtotals and a
// grand-total row — so a spanning (report) table matches the on-screen
// dashboard table instead of silently dropping its subtotals. Mirrors the
// grouping logic in TableRenderer (cfg.groupBy / cfg.subtotals / cfg.grandTotals).
function buildTableRows({ visual, cfg, data }) {
  const rowDims = visual.rowDims || [];
  const measures = visual.valueMeasures || [];
  const c = cfg || {};
  const groupByDims = (Array.isArray(c.groupBy) && c.groupBy.length)
    ? c.groupBy
    : ((c.subtotals && rowDims.length >= 2) ? [rowDims[0]] : []);
  const hasGrouping = groupByDims.length > 0;
  const agg = c.groupAggregation || 'sum';
  const out = [];
  const keyOf = (r) => groupByDims.map(d => String(r[d] ?? '')).join('|||');

  if (hasGrouping) {
    const counts = {};
    (data || []).forEach(r => { const k = keyOf(r); counts[k] = (counts[k] || 0) + 1; });
    let lastKey = null, acc = null, labels = null;
    const flush = () => {
      if (acc && c.groupFooter !== false) {
        const totals = {};
        measures.forEach(m => {
          totals[m] = agg === 'avg' ? (acc._count ? acc[m] / acc._count : 0)
            : agg === 'count' ? acc._count : acc[m];
        });
        out.push({ type: 'subtotal', labels, totals });
      }
      acc = null;
    };
    (data || []).forEach(r => {
      const k = keyOf(r);
      if (k !== lastKey) {
        flush();
        lastKey = k; labels = k.split('|||'); acc = { _count: 0 };
        out.push({ type: 'group_header', labels, count: counts[k] || 0 });
      }
      out.push({ type: 'data', row: r });
      acc._count += 1;
      measures.forEach(m => { acc[m] = (acc[m] || 0) + (typeof r[m] === 'number' ? r[m] : 0); });
    });
    flush();
  } else {
    (data || []).forEach(r => out.push({ type: 'data', row: r }));
  }

  if (c.grandTotals && measures.length) {
    const totals = {};
    measures.forEach(m => { totals[m] = (data || []).reduce((s, r) => s + (typeof r[m] === 'number' ? r[m] : 0), 0); });
    out.push({ type: 'grand_total', totals });
  }
  return out;
}

function buildPhysicalPages({ pages, visuals, globalSpan, resultOf, resolveData, vizConfigFor, bodyH, contentW }) {
  const phys = [];
  const spansFor = (v) => (v.spanPages !== undefined ? v.spanPages : (globalSpan !== false));
  pages.forEach((page, authoredIdx) => {
    const onPage = visuals.filter(v => (v._reportPageIndex ?? 0) === authoredIdx && !v._zone);
    const spanTable = onPage.find(v => v.vizType === 'table' && spansFor(v) && (resultOf(v)?.data?.length || 0) > 0);

    if (!spanTable) {
      phys.push({ authoredIdx, page, visuals: onPage, spanTable: null });
      return;
    }

    const fp = spanTable.freePosition || { x: 0, y: 0, w: contentW, h: 200 };
    const tableY = Math.max(0, fp.y || 0);
    const above = onPage.filter(v => v.id !== spanTable.id && ((v.freePosition?.y || 0) + (v.freePosition?.h || 0)) <= tableY + 4);
    const below = onPage.filter(v => v.id !== spanTable.id && !above.includes(v));
    const srcData = (resolveData ? resolveData(spanTable) : resultOf(spanTable)?.data) || [];
    const cfg = vizConfigFor ? (vizConfigFor(spanTable.id) || {}) : {};
    const typedRows = buildTableRows({ visual: spanTable, cfg, data: srcData });

    const firstAvail = Math.max(BODY_ROW_H * 2, bodyH - tableY);
    const rowsFirst = Math.max(1, Math.floor((firstAvail - HEADER_ROW_H) / BODY_ROW_H));
    const rowsCont = Math.max(1, Math.floor((bodyH - HEADER_ROW_H - CONTINUED_LABEL_H) / BODY_ROW_H));

    // First physical page: everything above/beside the table + the table's first slice.
    phys.push({
      authoredIdx, page, visuals: above,
      spanTable: { visual: spanTable, rows: typedRows.slice(0, rowsFirst), cfg, tableY, continued: false, firstOfTable: true },
    });

    let cursor = rowsFirst;
    while (cursor < typedRows.length) {
      const end = Math.min(typedRows.length, cursor + rowsCont);
      phys.push({
        authoredIdx, page, visuals: [],
        spanTable: { visual: spanTable, rows: typedRows.slice(cursor, end), cfg, tableY: 0, continued: true, firstOfTable: false },
        continuation: true,
      });
      cursor = end;
    }

    // Visuals placed below the table flow onto a fresh page after it finishes.
    if (below.length) {
      phys.push({ authoredIdx, page, visuals: below, spanTable: null, afterTable: true });
    }
  });
  return phys;
}

function SpanningTable({ visual, rows, cfg, repeatHeader, firstOfTable, formatValue, allFields }) {
  const rowDims = visual.rowDims || [];
  const measures = visual.valueMeasures || [];
  const cols = [...rowDims, ...measures];
  const showHeader = firstOfTable || repeatHeader;
  const fmt = (v, c) => (formatValue ? formatValue(v, c) : (v === null || v === undefined ? '' : String(v)));
  const _cfg = cfg || {};
  const grandTotalLabel = _cfg.grandTotalLabel ?? 'Grand Total';
  const subtotalLabelFor = (group) => {
    const tmpl = _cfg.subtotalLabel ?? 'Subtotal';
    if (/\{group\}/.test(tmpl)) return tmpl.replace(/\{group\}/g, group ?? '');
    return group ? `${tmpl}: ${group}` : tmpl;
  };
  return (
    <table className="w-full table-fixed border-collapse text-[11px]" data-testid="span-table">
      <colgroup>
        {cols.map(c => (
          <col key={c} style={measures.includes(c) ? { width: 110 } : undefined} />
        ))}
      </colgroup>
      {showHeader && (
        <thead>
          <tr>
            {cols.map(c => (
              <th key={c} className={`font-semibold text-gray-700 border-b-2 border-gray-300 px-2 py-1.5 bg-gray-50 align-bottom ${measures.includes(c) ? 'text-right' : 'text-left break-words'}`} style={{ height: HEADER_ROW_H }}>
                {resolveLabel(allFields, c) || c.replace(/_/g, ' ')}
              </th>
            ))}
          </tr>
        </thead>
      )}
      <tbody>
        {(rows || []).map((item, i) => {
          if (item.type === 'group_header') {
            return (
              <tr key={'gh_' + i} className="bg-gray-100" data-testid="span-group-header">
                <td colSpan={cols.length} className="px-2 font-bold text-gray-800" style={{ height: BODY_ROW_H }}>
                  {item.labels.join(' / ')}<span className="ml-1 font-normal text-gray-400">({item.count} rows)</span>
                </td>
              </tr>
            );
          }
          if (item.type === 'subtotal' || item.type === 'grand_total') {
            const isGrand = item.type === 'grand_total';
            return (
              <tr key={(isGrand ? 'gt_' : 'sub_') + i} className={isGrand ? 'bg-gray-100 border-t-2 border-gray-400 font-bold' : 'bg-gray-50 border-t border-gray-300 font-semibold'} data-testid={isGrand ? 'span-grand-total' : 'span-subtotal'}>
                {cols.map((c, ki) => (
                  <td key={c} className={`px-2 text-gray-800 overflow-hidden text-ellipsis ${measures.includes(c) ? 'text-right whitespace-nowrap' : 'whitespace-nowrap'}`} style={{ height: BODY_ROW_H }}>
                    {ki === 0 ? (isGrand ? grandTotalLabel : subtotalLabelFor(item.labels ? item.labels.join(' / ') : '')) : (measures.includes(c) ? fmt(item.totals[c] || 0, c) : '')}
                  </td>
                ))}
              </tr>
            );
          }
          const row = item.row;
          return (
            <tr key={'d_' + i} className={i % 2 ? 'bg-gray-50/60' : ''}>
              {cols.map(c => (
                <td key={c} className={`px-2 py-1 border-b border-gray-200 text-gray-800 align-top ${measures.includes(c) ? 'text-right whitespace-nowrap' : 'whitespace-normal break-words'}`} style={{ minHeight: BODY_ROW_H }}>
                  {fmt(row[c], c)}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export function ReportPagedViewer({
  config, reportName, themeLogo, visuals, resultOf, resolveData,
  renderBody, formatValueFor, allFields, vizConfigFor,
}) {
  const pageSize = config.reportPageSize || 'letter';
  const orientation = config.reportOrientation || 'portrait';
  const margins = config.reportMargins || { top: 48, right: 48, bottom: 48, left: 48 };
  const header = config.reportHeader || { enabled: true, height: 60, repeatOnPages: true, showOnFirstPage: true, title: '{{report_name}}' };
  const footer = config.reportFooter || { enabled: true, height: 40, text: 'Page {{page}} of {{total_pages}}', fontSize: 9 };
  const span = config.reportTableSpanPages !== false;
  const repeatHeader = config.reportTableRepeatHeader !== false;
  const authoredPages = (config.reportPages && config.reportPages.length) ? config.reportPages : [{ id: 'rp_1', name: 'Page 1' }];

  const dims = PAGE_DIMS[pageSize] || PAGE_DIMS.letter;
  const pageW = orientation === 'portrait' ? dims.w : dims.h;
  const pageH = orientation === 'portrait' ? dims.h : dims.w;
  const contentW = pageW - (margins.left || 48) - (margins.right || 48);
  const contentH = pageH - (margins.top || 48) - (margins.bottom || 48);
  const headerH = header.enabled ? (header.height || 60) : 0;
  const footerH = footer.enabled ? (footer.height || 40) : 0;
  const bodyH = contentH - headerH - footerH;

  const physical = useMemo(
    () => buildPhysicalPages({ pages: authoredPages, visuals, globalSpan: span, resultOf, resolveData, vizConfigFor, bodyH, contentW }),
    [authoredPages, visuals, span, resultOf, resolveData, vizConfigFor, bodyH, contentW]
  );

  const total = physical.length;
  const [activeIdx, setActiveIdx] = useState(0);
  const scrollRef = useRef(null);

  const jump = (i) => {
    setActiveIdx(i);
    const el = scrollRef.current?.querySelector(`[data-phys-page="${i}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    const root = scrollRef.current;
    if (!root) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) setActiveIdx(Number(e.target.getAttribute('data-phys-page'))); });
    }, { root, threshold: 0.5 });
    root.querySelectorAll('[data-phys-page]').forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, [total]);

  const renderHeader = (physIdx) => {
    if (!header.enabled) return null;
    const isFirst = physIdx === 0;
    if (!isFirst && header.repeatOnPages === false) return <div style={{ height: headerH }} />;
    if (isFirst && header.showOnFirstPage === false) return <div style={{ height: headerH }} />;
    return (
      <div className="flex items-center justify-between border-b border-gray-200 px-1" style={{ height: headerH }}>
        <div className="flex items-center gap-2">
          {(header.logoUrl || themeLogo) && <img src={header.logoUrl || themeLogo} alt="" className="max-h-8 object-contain" />}
          <span className="text-gray-800" style={{ fontSize: header.titleFontSize || 16, fontWeight: header.titleBold === false ? 'normal' : 'bold' }}>
            {fmtTitle(header.title, reportName)}
          </span>
        </div>
      </div>
    );
  };

  const renderFooter = (physIdx) => {
    if (!footer.enabled) return null;
    return (
      <div className="flex items-center justify-center border-t border-gray-200 text-gray-500" style={{ height: footerH, fontSize: footer.fontSize || 9 }}>
        {fmtFooter(footer.text, physIdx, total)}
      </div>
    );
  };

  return (
    <div className="flex h-full gap-4" data-testid="report-paged-viewer">
      <style>{`
        @media print {
          .report-print-page { break-after: page; page-break-after: always; box-shadow: none !important; margin: 0 !important; }
          [data-testid="report-page-rail"] { display: none !important; }
        }
      `}</style>
      {/* Page rail */}
      {total > 1 && (
        <div className="hidden md:flex flex-col gap-2 overflow-auto pr-1 flex-shrink-0 w-24" data-testid="report-page-rail">
          {physical.map((p, i) => (
            <button key={i} onClick={() => jump(i)} data-testid={`page-rail-${i}`}
              className={`relative rounded-md border transition-all ${activeIdx === i ? 'border-primary ring-1 ring-primary/40' : 'border-border/40 hover:border-primary/30'}`}
              style={{ aspectRatio: `${pageW} / ${pageH}`, background: '#fff' }}>
              <span className="absolute inset-x-0 bottom-0 text-[9px] text-gray-500 bg-white/80">{i + 1}</span>
            </button>
          ))}
        </div>
      )}

      {/* Pages */}
      <div ref={scrollRef} className="flex-1 overflow-auto flex flex-col items-center gap-6 py-2" data-testid="report-pages-scroll">
        {physical.map((p, physIdx) => (
          <div
            key={physIdx}
            data-phys-page={physIdx}
            data-testid={`report-page-${physIdx}`}
            className="bg-white shadow-lg shadow-black/10 relative report-print-page"
            style={{ width: pageW, minHeight: pageH, flexShrink: 0 }}
          >
            <div style={{ position: 'absolute', top: margins.top || 48, left: margins.left || 48, width: contentW, height: contentH }}>
              {renderHeader(physIdx)}
              {/* Body */}
              <div style={{ position: 'relative', height: bodyH }}>
                {p.continuation && (
                  <div className="text-[10px] italic text-gray-400 mb-1" style={{ height: CONTINUED_LABEL_H }} data-testid="table-continued-label">
                    {fmtTitle(p.spanTable?.visual?.title || 'Table', reportName)} (continued)
                  </div>
                )}
                {/* Non-spanning / after-table visuals: stack for after-table pages, else absolute */}
                {(p.afterTable ? p.visuals : p.visuals).map((v, vi) => {
                  const fp = v.freePosition || { x: 0, y: 0, w: contentW, h: 160 };
                  const style = p.afterTable
                    ? { position: 'relative', width: '100%', height: fp.h || 160, marginBottom: 12 }
                    : { position: 'absolute', left: fp.x || 0, top: fp.y || 0, width: fp.w || contentW, height: fp.h || 160 };
                  return (
                    <div key={v.id} data-visual-id={v.id} style={style} className="overflow-hidden">
                      <VisualErrorBoundary resetKey={`${fp.w}x${fp.h}`} label={v.title || v.vizType}>
                        {renderBody(v)}
                      </VisualErrorBoundary>
                    </div>
                  );
                })}
                {/* Spanning table */}
                {p.spanTable && (
                  <div
                    data-visual-id={p.spanTable.visual.id}
                    style={{ position: 'absolute', left: 0, top: p.spanTable.tableY || 0, width: contentW }}
                  >
                    <VisualErrorBoundary resetKey={`${p.spanTable.rows?.length || 0}`} label={p.spanTable.visual.title || 'Table'}>
                    <SpanningTable
                      visual={p.spanTable.visual}
                      rows={p.spanTable.rows}
                      cfg={p.spanTable.cfg}
                      repeatHeader={repeatHeader}
                      firstOfTable={p.spanTable.firstOfTable}
                      formatValue={formatValueFor ? formatValueFor(p.spanTable.visual.id) : undefined}
                      allFields={allFields}
                    />
                    </VisualErrorBoundary>
                  </div>
                )}
              </div>
              {renderFooter(physIdx)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
